import { useOrders } from '@api/useOrders'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { modes, paneModes } from '@constants/orderWizard'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import { useModelConfig } from '@hooks/useModelConfig'
import { Form, notification } from 'antd'
import moment from 'moment'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { isDefined } from '../../../utils/general'
import { getInitialMarkerState } from '../../_SiteWide/TankDisplay/TankGraphWidget'
import { useCarrierWindowOverride } from '../Panes/DisplayPane/DisplayComponents/EditedAlert/AlertPanes/useCarrierWindowOverride'
import { useDestinationsContext } from './DestinationsContext'
import dayjs from 'dayjs'
import { updateDriverAndScheduleIds } from './util'
const WizardMasterContext = createContext()

export function WizardMasterContextProvider({ children }) {
  const [wizardOrder, setWizardOrder] = useState(undefined)
  const [orderStops, setOrderStops] = useState(undefined)
  const [mode, setMode] = useState(modes.CREATE)
  const [paneMode, setPaneMode] = useState(paneModes.PRODUCTSELECT)
  const [orderEdited, setOrderEdited] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [payload, setPayload] = useState(undefined)
  const [isBackhaul, setIsBackhaul] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isShortLoad, setIsShortLoad] = useState(false)
  const [isEditingBackhaul, setIsEditingBackhaul] = useState(false)
  const [viewingNoteForm, setViewingNoteForm] = useState(false)
  const { setSelectedTank } = useDestinationsContext()
  const [driverNote, setDriverNote] = useState(undefined)
  const [form] = Form.useForm()
  const { reorderDropsMutation } = useOrders()
  const { scopes: auth } = useSecurityContext()
  const { updateSetting } = useUserSettings()
  const [isOrderChanged, setIsOrderChanged] = useState(false)

  const { value: rundownToggled, setValue: setRundownToggled } = useLocalStorage('show_projected_drops', true)

  const { value: graphMarkers, setValue: setGraphMarkers } = useLocalStorage(
    'tank_rundown_graph_markers',
    getInitialMarkerState()
  )

  useEffect(() => {
    if (!localStorage.getItem('default_entry_mode')) {
      localStorage.setItem('default_entry_mode', 'by_compartment')
    }
  }, [])

  const defaultEntryMode = localStorage.getItem('default_entry_mode')

  const currentEntryMode = useMemo(
    () =>
      isDefined(wizardOrder?.is_by_product)
        ? wizardOrder.is_by_product
          ? 'by_product'
          : 'by_compartment'
        : defaultEntryMode,
    [wizardOrder?.is_by_product, defaultEntryMode]
  )

  async function handleEntryModeChange(mode) {
    // if (currentEntryMode === mode) return
    // When the user changes modes, update the default selection and wipe compartments
    localStorage.setItem('default_entry_mode', mode)
    setWizardOrder({ ...wizardOrder, is_by_product: mode === 'by_product', compartments: [] })
    setPayload({ ...payload, is_by_product: mode === 'by_product', compartments: [] })
    await updateSetting('default_entry_mode', mode)
  }

  const toggleGraphMarker = (key) => setGraphMarkers({ ...graphMarkers, [key]: !graphMarkers[key] })

  const { config } = useModelConfig()
  const showAdditiveCheckbox = useMemo(() => config?.show_order_wizard_additive_checkbox, [config])

  const isAdditiveCheckboxEnabled = useMemo(
    () => wizardOrder?.state !== 'complete' && auth?.scopes?.includes('o.w'),
    [auth.scopes, wizardOrder]
  )
  const {
    useBackhaulMetadataQuery,
    saveBackhaulMutation,
    fixUpLoadedSupplyMutation,
    createManualOrderMutation,
    cancelOrderMutation,
  } = useOrders()
  const { data: backhaulMetadata } = useBackhaulMetadataQuery()

  const carrierWindowState = useCarrierWindowOverride({ payload, setPayload, wizardOrder })

  const backhaulMeta = useMemo(() => {
    if (backhaulMetadata) {
      const object = {
        products: backhaulMetadata?.products.sort(),
        counterparties: backhaulMetadata?.counterparties.sort(),
        locations: backhaulMetadata?.locations.sort(),
      }
      return object
    }
  }, [backhaulMetadata])

  function handleUpdateSupplyOptions(setSelectedSupply, supplyOption, isBest) {
    const getFlags = (order) => {
      if (isBest && order?.flags) {
        return (order.flags = order.flags.filter((f) => f.type !== 'supply_option' && f.type !== 'optimal_supply'))
      }
    }

    const driver = wizardOrder?.assigned_drivers?.[0] // you can only edit orders with one driver
    const driverScheduleId = wizardOrder?.assigned_driver_schedules?.[0]
    const { loads, drops, detours } = updateDriverAndScheduleIds(supplyOption, driver, driverScheduleId)

    const orderCopy = {
      ...wizardOrder,
      compartments: supplyOption.payload.compartments,
      drops,
      loads,
      affected_tanks: supplyOption.affected_tanks,
      total_gallons: supplyOption.total_gallons,
      load_type: supplyOption.load_type,
      unload_time: supplyOption.unload_time,
      estimated_travel_time: supplyOption.loaded_miles,
      supply_option: supplyOption,
      flags: getFlags(wizardOrder),
      detours,
    }

    const payloadCopy = {
      ...supplyOption.payload,
      supply_option: {
        ...supplyOption,
        payload: undefined,
        affected_tanks: undefined,
        option: isBest ? 'Best Option' : '',
      },
      is_short_load: isShortLoad,
      is_best_supply: isBest,
      carrier_window_start: orderCopy.carrier_window_start,
      carrier_window_end: orderCopy.carrier_window_end,
      carrier_window_override: orderCopy.carrier_window_override,
      carrier: orderCopy.carrier,
      carrier_id: orderCopy.carrier_id,
      state: orderCopy.state ? orderCopy.state : 'accepted',
    }

    payloadCopy.compartments.forEach((c) => {
      c.volume = c.targets[0].volume
      if (c.targets[1]) c.volume += c.targets[1].volume
    })
    setWizardOrder(orderCopy)
    setOrderStops(supplyOption.payload.map_stops)
    setSelectedSupply(supplyOption)
    !isBest && setOrderEdited(true)
    setPayload(payloadCopy)
    handleChangeSupplyLock(false)
    setPaneMode(paneModes.TANKIMPACT)
  }

  // key can ONLY be 'loads' or 'drops'
  const resequenceOrderStops = async (oldIndex, newIndex, key) => {
    const orderCopy = { ...wizardOrder }
    const keyCopy = [...orderCopy[key]]
    const waitingRoom = keyCopy[oldIndex]
    keyCopy[oldIndex] = keyCopy[newIndex]
    keyCopy[newIndex] = waitingRoom
    orderCopy[key] = keyCopy

    const data = await reorderDropsMutation.mutateAsync({
      drops: orderCopy.drops,
      loads: orderCopy.loads,
      supply_option: orderCopy.supply_option,
      compartments: orderCopy.compartments,
      order_id: orderCopy.id,
    })

    setOrderStops(data.map_stops)
    orderCopy.supply_option.loaded_miles = data.updated_loaded_miles
    orderCopy.supply_option.landed_cost = data.updated_total
    setOrderEdited(true)
    setWizardOrder(orderCopy)
    setPayload({ ...payload, drops: orderCopy.drops, loads: orderCopy.loads })
    mode === modes.VIEW && setMode(modes.EDIT)
  }
  const fixUpLoadedOrderSupplyOption = async (payload) => {
    setIsSaving(true)

    const data = await fixUpLoadedSupplyMutation.mutateAsync(payload)
    data.compartments.forEach((c) => {
      c.volume = c.targets[0].volume
      if (c.targets[1]) c.volume += c.targets[1].volume
    })
    setPayload({ ...data, is_short_load: isShortLoad })
    setWizardOrder({ ...data })
    setPaneMode(paneModes.TANKIMPACT)
    setSelectedTank(undefined)

    setIsSaving(false)
  }
  const createBackhaulPayload = (order) => {
    setPayload({ ...order, volume: 0 })
  }
  const updateBackhaulField = (accessor, value) => {
    const copy = { ...payload }

    if (accessor === 'carrier_code' || accessor === 'load_number') {
      copy.extra_data[accessor] = value
    }
    if (accessor === 'load_location') {
      copy.loads[0].location_name = value
    }
    if (accessor === 'load_counterparty') {
      copy.loads[0].details[0].counterparty = value
    }
    if (accessor === 'product') {
      copy.loads[0].details[0].product_name = value
      copy.drops[0].details[0].product_name = value
    }
    if (accessor === 'drop_location') {
      copy.drops[0].location_name = value
    }

    if (accessor === 'drop_counterparty') {
      copy.drops[0].counterparty = value
    } else {
      copy[accessor] = value
    }
    setPayload(copy)
  }
  const updateBackhaulWindow = (dates) => {
    const copy = { ...payload }
    copy.load_window_start = dates[0]
    copy.load_window_end = dates[1]
    copy.dispatch_start = dates[0]
    copy.dispatch_end = dates[1]
    setPayload(copy)
  }

  const initializeSelectedStatus = (order, setSelectedStatus) => {
    if (order && order.flags && order.flags.length > 0) {
      const unavailableFlag = order.flags.find((f) => f.type === 'supply_option')
      const subOptimalFlag = order.flags.find((f) => f.type === 'optimal_supply')
      if (unavailableFlag) {
        setSelectedStatus('invalid')
      } else if (subOptimalFlag) {
        setSelectedStatus(subOptimalFlag.severity)
      } else {
        setSelectedStatus('')
      }
    }
  }

  const modelGenerated = () => {
    return wizardOrder && wizardOrder.is_model_created
  }

  const make_compartments = (order) => {
    const compartments = order.compartments.map((c) => {
      return { ...c, volume: c.targets.reduce((a, b) => a + (b.volume || 0), 0) }
    })
    return { compartments }
  }

  const saveDriverNote = (note) => {
    setDriverNote(note.content)
    setViewingNoteForm(false)
    setIsOrderChanged(true)
  }

  const saveOrder = async ({ closeModal, resendToCarrier = false, resendToSupplier = false, trailerOverride }) => {
    const { detours, ...payloadWithoutDetours } = payload
    const manualOrder = {
      ...wizardOrder,
      ...payloadWithoutDetours,
      ...make_compartments(wizardOrder),
      order_id: wizardOrder.id,
      order_type: 'regular',
      note: {
        content: driverNote,
      },
      carrier_window_start: payload?.carrier_window_start && moment(payload?.carrier_window_start)?.toISOString(false),
      carrier_window_end: payload?.carrier_window_end && moment(payload?.carrier_window_end)?.toISOString(false),
    }
    setIsSaving(true)
    const sending = {
      payload: {
        ...manualOrder,
        trailer_config: trailerOverride,
        is_by_product: currentEntryMode === 'by_product',
        revision: wizardOrder.revision,
      },
      resend: resendToCarrier,
      send_to_supplier: resendToSupplier,
    }
    try {
      const data = await createManualOrderMutation.mutateAsync(sending)
      // When a crossroads order that's been sent has changes made to it, the API will send back null
      // indicating that changes have been requested but the order actually hasn't been udpated yet.
      if (typeof data === 'undefined' || data === null) {
        notification.success({
          message: 'Changes Requested',
          description: `Your order changes have been requested`,
        })
      } else {
        notification.success({
          message: 'Order Saved',
          description: `Your order was saved successfully. ${data.number ? `(Order No. ${data.number})` : ''}`,
        })
      }
      if (data && !data.unresolved_flags) {
        data.unresolved_flags = data.flags?.filter((flag) => flag.reason_code === null)
      }
      const shouldRefresh = data?.state === 'in progress' || data?.state === 'assigned'
      setIsSaving(false)
      form.resetFields()
      closeModal(false, data, shouldRefresh)
    } catch {
      setIsSaving(false)
      setDriverNote('')
      form.resetFields()
    }
  }

  const cancelOrder = async (note) => {
    await cancelOrderMutation.mutateAsync({ order_id: wizardOrder.id, note })
  }

  const saveBackhaul = async ({ closeModal } = {}) => {
    setIsSaving(true)
    try {
      const data = await saveBackhaulMutation.mutateAsync({ ...payload, note: { content: driverNote } })
      setIsSaving(false)
      setIsEditingBackhaul(false)
      setWizardOrder(data)
      setOrderStops(data.map_stops)
      setIsComplete(true)
    } finally {
      setIsSaving(false)
      if (closeModal) {
        closeModal()
      }
    }
  }

  function handleChangeSupplyLock(isLocked) {
    setWizardOrder((prev) => ({
      ...prev,
      supply_locked: isLocked,
    }))
    setIsOrderChanged(true)
  }

  function handleAddDetour(detour) {
    setWizardOrder((prev) => {
      const detours = prev.detours || []
      return {
        ...prev,
        detours: [...detours, detour].sort((a, b) => dayjs(a.completed_at).valueOf() - dayjs(b.completed_at).valueOf()),
      }
    })
    setIsOrderChanged(true)
  }

  function handleDeleteDetour(detourId) {
    setWizardOrder((prev) => {
      if (prev.detours) {
        prev.detours = prev.detours.filter((d) => {
          if (d.detour_id) return d.detour_id !== detourId
          return d.temp_id !== detourId // temp_id is used for new detours that haven't been saved yet
        })
      }
      return { ...prev }
    })
    setIsOrderChanged(true)
  }

  return (
    <WizardMasterContext.Provider
      value={{
        config,
        wizardOrder,
        orderStops,
        mode,
        paneMode,
        orderEdited,
        isComplete,
        viewingNoteForm,
        form,
        setViewingNoteForm,
        driverNote,
        setDriverNote,
        saveDriverNote,
        payload,
        isBackhaul,
        isSaving,
        isEditingBackhaul,
        isShortLoad,
        saveOrder,
        saveBackhaul,
        fixUpLoadedOrderSupplyOption,
        cancelOrder,
        handleUpdateSupplyOptions,
        initializeSelectedStatus,
        modelGenerated,
        setWizardOrder,
        setOrderStops,
        setIsShortLoad,
        setMode,
        setPaneMode,
        setOrderEdited,
        setIsComplete,
        setPayload,
        setIsBackhaul,
        setIsSaving,
        setIsEditingBackhaul,
        createBackhaulPayload,
        updateBackhaulField,
        updateBackhaulWindow,
        backhaulMeta,
        resequenceOrderStops,
        rundownToggled,
        setRundownToggled,
        graphMarkers,
        setGraphMarkers,
        toggleGraphMarker,
        carrierWindowState,
        showAdditiveCheckbox,
        isAdditiveCheckboxEnabled,
        defaultEntryMode,
        currentEntryMode,
        handleEntryModeChange,
        handleChangeSupplyLock,
        isOrderChanged,
        setIsOrderChanged,
        handleAddDetour,
        handleDeleteDetour,
      }}
    >
      {children}
    </WizardMasterContext.Provider>
  )
}

export const useWizardMaster = () => {
  const context = useContext(WizardMasterContext)
  if (context === undefined) {
    throw new Error('Context must be used within a Provider')
  }
  return context
}
